/**
 * 项目管理页面
 * 新视觉设计（参考设计稿）：居中标题 + 大搜索框 + 标签筛选 + 三列卡片网格
 * 展示所有项目，支持搜索、筛选、编辑、复制、删除
 */
import React, { useState, useEffect } from 'react';
import {
  FunnelIcon,
  ArrowPathIcon,
} from '@heroicons/react/24/outline';
import { ProjectInfo, ProjectType } from '../types';
import { projectApi, projectGroupApi, authApi } from '../services/api';
import ConfirmDialog from '../components/ConfirmDialog';
import Toast, { ToastType } from '../components/Toast';
import LoadingOverlay from '../components/LoadingOverlay';

// 设计稿切图资源
import iconSearch from '../assets/design/搜索.svg';
import iconGroup from '../assets/design/项目组.svg';
import iconDoc from '../assets/design/标书图标.png';
import ribbonMain from '../assets/design/主标.png';
import ribbonSub from '../assets/design/副标.png';
import iconWords from '../assets/design/字数.png';
import iconCompany from '../assets/design/公司.svg';
import iconToken from '../assets/design/token.svg';
import iconTime from '../assets/design/时间.svg';
import iconEdit from '../assets/design/编辑.svg';
import iconCopy from '../assets/design/复制.svg';
import iconDelete from '../assets/design/删除.svg';

interface ProjectManagementProps {
  onEditProject?: (projectId: string, currentStep?: number) => void;
}

const ProjectManagement: React.FC<ProjectManagementProps> = ({ onEditProject }) => {
  const [searchText, setSearchText] = useState('');
  const [selectedType, setSelectedType] = useState<ProjectType | ''>('');
  const [selectedGroupId, setSelectedGroupId] = useState<string>(''); // 项目组过滤
  const [projectGroups, setProjectGroups] = useState<{id: string; name: string; is_default: boolean}[]>([]); // 项目组列表
  const [myProjectGroups, setMyProjectGroups] = useState<{id: string; name: string; is_default: boolean}[]>([]); // 我创建的项目组（复制用）
  const [projects, setProjects] = useState<ProjectInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentUser, setCurrentUser] = useState<{id: number; username: string; display_name: string} | null>(null); // 当前用户
  const [deleteTarget, setDeleteTarget] = useState<ProjectInfo | null>(null);
  const [copyingId, setCopyingId] = useState<string | null>(null); // 复制中的项目id
  const [copyLoading, setCopyLoading] = useState({ show: false, message: '' }); // 复制加载状态
  const [deleteLoading, setDeleteLoading] = useState({ show: false, message: '' }); // 删除加载状态

  // 复制项目弹窗状态
  const [copyModal, setCopyModal] = useState<{
    show: boolean;
    project: ProjectInfo | null;
    newName: string;
    selectedGroupId: string;
  }>({ show: false, project: null, newName: '', selectedGroupId: '' });

  const [toast, setToast] = useState<{ show: boolean; type: ToastType; title: string; messages?: string[] }>({
    show: false,
    type: 'info',
    title: ''
  });

  const showToast = (type: ToastType, title: string, messages?: string[]) => {
    setToast({ show: true, type, title, messages });
  };

  const closeToast = () => {
    setToast({ ...toast, show: false });
  };

  // 格式化Token数量（K/M格式）
  const formatTokens = (tokens: number = 0) => {
    if (tokens >= 1000000) {
      return `${(tokens / 1000000).toFixed(1)}M`;
    }
    if (tokens >= 1000) {
      return `${(tokens / 1000).toFixed(1)}K`;
    }
    return tokens.toLocaleString();
  };

  // 打开复制弹窗
  const openCopyModal = (project: ProjectInfo, e: React.MouseEvent) => {
    e.stopPropagation();
    // 检查登录状态
    if (!currentUser) {
      showToast('warning', '请先登录', ['无法获取当前用户信息']);
      return;
    }
    // 找到默认项目组作为默认选中
    const defaultGroup = myProjectGroups.find((g) => g.is_default);
    const targetGroupId = defaultGroup?.id || (myProjectGroups[0]?.id ?? '');

    setCopyModal({
      show: true,
      project,
      newName: `${project.alias?.trim() || project.projectName} - 副本`,
      selectedGroupId: targetGroupId,
    });
  };

  // 后端项目数据 → 前端格式
  const mapProject = (p: any): ProjectInfo => ({
    id: p.id,
    projectName: p.name,
    alias: p.alias || '',
    projectType: p.project_type || '数据类',
    wordCount: p.word_count || 50000,
    companyName: p.company?.name || '未知主体',
    creator: p.user?.display_name || p.user?.username || '系统',
    userId: p.user_id,
    createdAt: new Date(p.created_at + 'Z').toLocaleString('zh-CN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    }),
    updatedAt: new Date(p.updated_at + 'Z').toLocaleString('zh-CN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    }),
    status: p.status || 'draft',
    currentStep: p.progress?.current_step || 0,
    maxReachedStep: p.progress?.max_reached_step || 0,
    tokenUsage: p.token_usage || 0,
    groupId: p.group_id || undefined,
  });

  // 执行复制
  const executeCopy = async () => {
    if (!copyModal.project) return;

    try {
      setCopyLoading({ show: true, message: '正在复制项目数据...' });
      setCopyingId(copyModal.project.id);

      // 调用后端API复制项目（传入新名称和项目组）
      await projectApi.copyProject(copyModal.project.id, {
        alias: copyModal.newName,
        group_id: copyModal.selectedGroupId || undefined,
      });

      // 关闭弹窗
      setCopyModal({ show: false, project: null, newName: '', selectedGroupId: '' });

      // 重新加载项目列表
      const projectsResponse = await projectApi.getProjects();
      setProjects(projectsResponse.data.map(mapProject));

      setCopyLoading({ show: false, message: '' });
      showToast('success', '项目复制成功！', [`已从「${copyModal.project.projectName}」创建副本`]);
    } catch (error) {
      console.error('复制项目失败:', error);
      setCopyLoading({ show: false, message: '' });
      showToast('error', '复制项目失败', ['请稍后重试']);
    } finally {
      setCopyingId(null);
    }
  };

  // 加载项目列表与项目组
  const loadData = async () => {
    try {
      setLoading(true);

      // 获取当前用户信息
      try {
        const userRes = await authApi.getCurrentUser();
        setCurrentUser(userRes.data);
      } catch {
        // 未登录或获取失败
      }

      const [projectsRes, groupsRes] = await Promise.all([
        projectApi.getProjects(),
        projectGroupApi.getSimpleList(),
      ]);

      const groups = groupsRes.data || [];
      setProjectGroups(groups);

      // 只显示自己创建的项目组（owner）或默认分组
      const myGroups = groups.filter((g: any) => g.is_owner || g.my_role === 'owner' || g.is_default);
      setMyProjectGroups(myGroups);

      setProjects(projectsRes.data.map(mapProject));
    } catch (error) {
      console.error('加载项目列表失败:', error);
    } finally {
      setLoading(false);
    }
  };

  // 从数据库加载项目列表
  useEffect(() => {
    loadData();
  }, []);

  // 监听全局刷新数据事件（登录成功后触发）
  useEffect(() => {
    const handleRefreshData = () => {
      console.log('[ProjectManagement] 收到刷新数据事件');
      loadData();
    };

    window.addEventListener('app:refresh-data', handleRefreshData);
    return () => window.removeEventListener('app:refresh-data', handleRefreshData);
  }, []);

  const projectTypes: Array<ProjectType | ''> = ['', '通用类', '数据类', '定开类', '产品类', '两轮车'];

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      setDeleteLoading({ show: true, message: '正在删除项目...' });

      await projectApi.deleteProject(deleteTarget.id);
      setProjects((prev) => prev.filter((p) => p.id !== deleteTarget.id));

      setDeleteLoading({ show: false, message: '' });
      setDeleteTarget(null);
      showToast('success', '项目删除成功！', [`已删除「${deleteTarget.projectName}」`]);
    } catch (error) {
      console.error('删除项目失败:', error);
      setDeleteLoading({ show: false, message: '' });
      showToast('error', '删除项目失败', ['请稍后重试']);
    }
  };

  // 筛选项目
  const filteredProjects = projects.filter((project) => {
    const matchSearch =
      project.projectName.toLowerCase().includes(searchText.toLowerCase()) ||
      (project.alias && project.alias.toLowerCase().includes(searchText.toLowerCase())) ||
      project.companyName.toLowerCase().includes(searchText.toLowerCase()) ||
      project.creator.toLowerCase().includes(searchText.toLowerCase());

    const matchType = !selectedType || project.projectType === selectedType;
    const matchGroup = !selectedGroupId || project.groupId === selectedGroupId;

    return matchSearch && matchType && matchGroup;
  });

  // 标签筛选按钮样式
  const tabClass = (isActive: boolean) =>
    `flex items-center gap-1.5 text-[15px] tracking-wide px-[14px] py-[6px] rounded-lg whitespace-nowrap transition-colors ${
      isActive
        ? 'bg-[#EEEFF2] text-[#26292C] font-semibold'
        : 'text-[#575C62] hover:bg-[#EEEFF2]'
    }`;

  return (
    <div className="h-full flex flex-col bg-transparent overflow-y-auto px-8 pt-5 pb-8">
      {/* 复制加载蒙层 */}
      <LoadingOverlay show={copyLoading.show} message={copyLoading.message} />

      {/* 删除加载蒙层 */}
      <LoadingOverlay show={deleteLoading.show} message={deleteLoading.message} />

      {/* Toast 通知 */}
      <Toast
        show={toast.show}
        type={toast.type}
        title={toast.title}
        messages={toast.messages}
        onClose={closeToast}
        duration={3000}
      />

      {/* 页面标题 */}
      <h1 className="text-[26px] font-bold tracking-widest text-center text-[#26292C] flex-shrink-0">项目管理</h1>

      {/* 搜索框 */}
      <div
        className="mt-[26px] mx-auto w-[660px] max-w-full h-14 flex-shrink-0 bg-white rounded-[14px] flex items-center gap-3 px-[22px] transition-shadow focus-within:shadow-[0_4px_16px_rgba(31,112,255,.12)]"
        style={{ border: '1px solid rgba(0,0,0,.04)', boxShadow: '0 2px 10px rgba(31,41,55,.05)' }}
      >
        <img src={iconSearch} alt="" className="w-[18px] h-[18px] flex-shrink-0" />
        <input
          type="text"
          placeholder="搜索项目别名、项目名、主体、创建人…"
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          className="flex-1 min-w-0 border-none outline-none bg-transparent text-[15px] text-[#26292C] tracking-wide placeholder-[#9AA0A6]"
        />
      </div>

      {/* 筛选行 + 卡片网格容器 */}
      <div className="w-full max-w-[1332px] mx-auto">
        {/* 筛选行：左侧类型标签，右侧项目组标签 */}
        <div className="mt-[26px] flex items-center flex-wrap gap-y-2">
          <div className="flex items-center gap-2 flex-wrap">
            {projectTypes.map((type) => (
              <button
                key={type || 'all'}
                type="button"
                onClick={() => setSelectedType(type)}
                className={tabClass(selectedType === type)}
              >
                {type || '全部'}
              </button>
            ))}
          </div>
          {projectGroups.length > 0 && (
            <div className="flex items-center gap-2 flex-wrap ml-auto">
              <button
                type="button"
                onClick={() => setSelectedGroupId('')}
                className={tabClass(selectedGroupId === '')}
              >
                <img src={iconGroup} alt="" className="w-4 h-4 object-contain" />全部组
              </button>
              {[...projectGroups]
                .sort((a, b) => (b.is_default ? 1 : 0) - (a.is_default ? 1 : 0))
                .map(g => (
                  <button
                    key={g.id}
                    type="button"
                    onClick={() => setSelectedGroupId(g.id)}
                    className={tabClass(selectedGroupId === g.id)}
                  >
                    <img src={iconGroup} alt="" className="w-4 h-4 object-contain" />{g.name}
                  </button>
                ))}
            </div>
          )}
        </div>

        {/* 项目卡片网格 */}
        <div className="mt-[18px] grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
          {filteredProjects.map((project) => {
            const isOwner = currentUser ? project.userId === currentUser.id : false;
            const groupName = project.groupId
              ? projectGroups.find(g => g.id === project.groupId)?.name
              : undefined;

            return (
              <article
                key={project.id}
                className="relative bg-white rounded-[14px] p-2 transition-all duration-200 hover:-translate-y-1"
                style={{ boxShadow: '0 2px 10px rgba(31,41,55,.05)' }}
              >
                {/* 主/副角标 */}
                {currentUser && (
                  <img
                    className="absolute -top-[5px] right-4 w-[33px] h-auto z-[2] pointer-events-none"
                    src={isOwner ? ribbonMain : ribbonSub}
                    alt={isOwner ? '主' : '副'}
                  />
                )}

                {/* 头部浅蓝渐变区 */}
                <div
                  className="rounded-[10px] px-5 pt-5 pb-[18px] flex items-center gap-4"
                  style={{ background: 'linear-gradient(180deg,#E9F2FF 0%,#DCE9FF 100%)' }}
                >
                  <img className="w-14 h-14 object-contain flex-shrink-0" src={iconDoc} alt="" />
                  <div className="flex-1 min-w-0">
                    <h3
                      className="text-lg font-bold text-[#26292C] tracking-wide whitespace-nowrap overflow-hidden text-ellipsis"
                      title={project.alias?.trim() || project.projectName}
                    >
                      {project.alias?.trim() || project.projectName}
                    </h3>
                    <div
                      className="mt-[7px] text-[13px] text-[#868D96] tracking-wide whitespace-nowrap overflow-hidden text-ellipsis"
                      title={project.alias?.trim() ? project.projectName : undefined}
                    >
                      {project.alias?.trim() ? project.projectName : ' '}
                    </div>
                    {/* 标签行 */}
                    <div className="mt-3 flex items-center gap-2 flex-wrap">
                      <span className="text-xs leading-tight rounded px-2 py-1 tracking-wide whitespace-nowrap bg-[#BCE3D8] text-[#0CAA3E]">
                        第 {(project.maxReachedStep || 0) + 1}/5 步
                      </span>
                      <span className="text-xs leading-tight rounded px-2 py-1 tracking-wide whitespace-nowrap bg-[#BED5FF] text-[#1F70FF]">
                        {project.projectType}
                      </span>
                      {groupName && (
                        <span className="text-xs leading-tight rounded px-2 py-1 tracking-wide whitespace-nowrap bg-[#D2DCED] text-[#575C62] inline-flex items-center gap-1">
                          <img src={iconGroup} alt="" className="w-[13px] h-[13px] object-contain" />
                          {groupName}
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                {/* 信息区 */}
                <div className="px-4 pt-[14px] pb-1.5 grid grid-cols-2 gap-y-3 gap-x-3">
                  <div className="flex items-center gap-2 text-sm text-[#575C62] tracking-wide whitespace-nowrap min-w-0">
                    <img src={iconWords} alt="" className="w-[15px] h-[15px] object-contain flex-shrink-0" />
                    <span className="truncate">{(project.wordCount / 10000).toFixed(1)}万字</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-[#575C62] tracking-wide whitespace-nowrap min-w-0">
                    <img src={iconCompany} alt="" className="w-[15px] h-[15px] object-contain flex-shrink-0" />
                    <span className="truncate" title={project.companyName}>{project.companyName}</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-[#575C62] tracking-wide whitespace-nowrap min-w-0">
                    <img src={iconToken} alt="" className="w-[15px] h-[15px] object-contain flex-shrink-0" />
                    <span className="truncate">{formatTokens(project.tokenUsage)} Tokens</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-[#575C62] tracking-wide whitespace-nowrap min-w-0">
                    <img src={iconTime} alt="" className="w-[15px] h-[15px] object-contain flex-shrink-0" />
                    <span className="truncate">{project.createdAt}</span>
                  </div>
                </div>

                {/* 操作区 */}
                <div className="flex justify-end items-center gap-6 px-4 pt-2.5 pb-3">
                  <button
                    className="flex items-center gap-1.5 text-sm text-[#1F70FF] tracking-wide rounded-md transition-opacity hover:opacity-75"
                    onClick={(e) => {
                      e.stopPropagation();
                      onEditProject?.(project.id, project.currentStep);
                    }}
                    title="编辑项目"
                  >
                    <img src={iconEdit} alt="" className="w-4 h-4 object-contain" />编辑
                  </button>
                  <button
                    className="flex items-center gap-1.5 text-sm text-[#1F70FF] tracking-wide rounded-md transition-opacity hover:opacity-75 disabled:opacity-50"
                    onClick={(e) => openCopyModal(project, e)}
                    disabled={copyingId === project.id}
                    title="复制项目"
                  >
                    {copyingId === project.id ? (
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-[#1F70FF]"></div>
                    ) : (
                      <img src={iconCopy} alt="" className="w-4 h-4 object-contain" />
                    )}复制
                  </button>
                  {/* 删除按钮 - 只有项目创建者才显示 */}
                  {isOwner && (
                    <button
                      className="flex items-center gap-1.5 text-sm text-[#1F70FF] tracking-wide rounded-md transition-opacity hover:opacity-75"
                      onClick={(e) => {
                        e.stopPropagation();
                        setDeleteTarget(project);
                      }}
                      title="删除项目"
                    >
                      <img src={iconDelete} alt="" className="w-4 h-4 object-contain" />删除
                    </button>
                  )}
                </div>
              </article>
            );
          })}
        </div>

        {/* 加载状态 */}
        {loading && (
          <div className="flex flex-col items-center justify-center h-64 text-gray-400">
            <ArrowPathIcon className="w-8 h-8 animate-spin mb-3" />
            <p className="text-base">加载中...</p>
          </div>
        )}

        {/* 空状态 */}
        {filteredProjects.length === 0 && !loading && (
          <div className="flex flex-col items-center justify-center h-64 text-gray-400">
            <FunnelIcon className="w-16 h-16 mb-4" />
            <p className="text-lg">暂无项目信息</p>
            <p className="text-sm mt-2">
              赶快去
              <button
                onClick={() => onEditProject?.('new')}
                className="text-[#1F70FF] hover:opacity-75 font-medium mx-1"
              >
                新建标书
              </button>
              吧
            </p>
          </div>
        )}
      </div>

      {/* 删除确认弹窗 */}
      <ConfirmDialog
        isOpen={!!deleteTarget}
        title="确认删除该项目？"
        message={deleteTarget ? `删除后将无法恢复。\n项目：${deleteTarget.projectName}` : ''}
        confirmText="删除"
        cancelText="取消"
        confirmButtonClass="bg-red-600 hover:bg-red-700 focus:ring-red-500"
        onConfirm={handleConfirmDelete}
        onCancel={() => setDeleteTarget(null)}
      />

      {/* 复制项目弹窗 */}
      {copyModal.show && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md mx-4 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">复制项目</h3>

            <div className="space-y-4">
              {/* 项目名称 */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">新项目名称</label>
                <input
                  type="text"
                  value={copyModal.newName}
                  onChange={(e) => setCopyModal(prev => ({ ...prev, newName: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  placeholder="请输入项目名称"
                />
              </div>

              {/* 项目组选择 */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">所属项目组</label>
                <select
                  value={copyModal.selectedGroupId}
                  onChange={(e) => setCopyModal(prev => ({ ...prev, selectedGroupId: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                >
                  {[...myProjectGroups]
                    .sort((a, b) => (b.is_default ? 1 : 0) - (a.is_default ? 1 : 0))
                    .map(g => (
                      <option key={g.id} value={g.id}>
                        {g.name}{g.is_default ? '（默认）' : ''}
                      </option>
                    ))}
                </select>
                {myProjectGroups.length === 0 && (
                  <p className="text-xs text-gray-500 mt-1">您还没有创建任何项目组</p>
                )}
              </div>
            </div>

            {/* 按钮 */}
            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => setCopyModal({ show: false, project: null, newName: '', selectedGroupId: '' })}
                className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
              >
                取消
              </button>
              <button
                onClick={executeCopy}
                disabled={!copyModal.newName.trim() || copyingId === copyModal.project?.id}
                className="px-4 py-2 text-white bg-green-600 hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg transition-colors"
              >
                {copyingId === copyModal.project?.id ? '复制中...' : '确认复制'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProjectManagement;
